import { useState } from "react";

import { Droplet, Package } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import { useCreateOilType, useCreateRawMaterial } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import type { OilTypeFormData, RawMaterialFormData } from "~/types/purchasing";
import { MATERIAL_UNITS } from "~/types/purchasing";

interface NewIngredientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "oil" | "material";
}

export function NewIngredientDialog({
  open,
  onOpenChange,
  defaultTab = "oil",
}: NewIngredientDialogProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"oil" | "material">(defaultTab);

  const createOilMutation = useCreateOilType();
  const createMaterialMutation = useCreateRawMaterial();

  const [oilFormData, setOilFormData] = useState<OilTypeFormData>({
    name: "",
    code: "",
    description: "",
    density_kg_per_liter: 0.92,
    minimum_stock_liters: undefined,
  });

  const [materialFormData, setMaterialFormData] = useState<RawMaterialFormData>(
    {
      name: "",
      code: "",
      description: "",
      unit: "UNI",
      minimum_stock_level: undefined,
      exclude_from_inventory: false,
    }
  );

  const resetForms = () => {
    setOilFormData({
      name: "",
      code: "",
      description: "",
      density_kg_per_liter: 0.92,
      minimum_stock_liters: undefined,
    });
    setMaterialFormData({
      name: "",
      code: "",
      description: "",
      unit: "UNI",
      minimum_stock_level: undefined,
      exclude_from_inventory: false,
    });
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForms();
    }
    onOpenChange(open);
  };

  const handleCreateOil = (e: React.FormEvent) => {
    e.preventDefault();

    if (!oilFormData.name.trim() || !oilFormData.code.trim()) {
      toast({
        title: "Error",
        description: "Nombre y código son requeridos",
        variant: "destructive",
      });
      return;
    }

    createOilMutation.mutate(oilFormData, {
      onSuccess: () => {
        toast({
          title: "Aceite creado",
          description: `${oilFormData.name} ha sido creado`,
        });
        handleClose(false);
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleCreateMaterial = (e: React.FormEvent) => {
    e.preventDefault();

    if (!materialFormData.name.trim() || !materialFormData.code.trim()) {
      toast({
        title: "Error",
        description: "Nombre y código son requeridos",
        variant: "destructive",
      });
      return;
    }

    createMaterialMutation.mutate(materialFormData, {
      onSuccess: () => {
        toast({
          title: "Material creado",
          description: `${materialFormData.name} ha sido creado`,
        });
        handleClose(false);
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const isPending =
    createOilMutation.isPending || createMaterialMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nuevo Ingrediente</DialogTitle>
          <DialogDescription>
            Selecciona el tipo de ingrediente y completa los datos
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "oil" | "material")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="oil" className="flex items-center gap-2">
              <Droplet className="h-4 w-4" />
              Aceite
            </TabsTrigger>
            <TabsTrigger value="material" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Material
            </TabsTrigger>
          </TabsList>

          <TabsContent value="oil" className="mt-4">
            <form onSubmit={handleCreateOil}>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="oil-code">Código *</Label>
                    <Input
                      id="oil-code"
                      value={oilFormData.code}
                      onChange={(e) =>
                        setOilFormData({
                          ...oilFormData,
                          code: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="OLV, CAN..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oil-density">Densidad (kg/L)</Label>
                    <Input
                      id="oil-density"
                      type="number"
                      step="0.001"
                      min="0.5"
                      max="1.5"
                      value={oilFormData.density_kg_per_liter}
                      onChange={(e) =>
                        setOilFormData({
                          ...oilFormData,
                          density_kg_per_liter:
                            parseFloat(e.target.value) || 0.92,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oil-name">Nombre *</Label>
                  <Input
                    id="oil-name"
                    value={oilFormData.name}
                    onChange={(e) =>
                      setOilFormData({ ...oilFormData, name: e.target.value })
                    }
                    placeholder="Aceite de Oliva Extra Virgen"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oil-min-stock">
                    Stock Mínimo (toneladas)
                  </Label>
                  <Input
                    id="oil-min-stock"
                    type="number"
                    min="0"
                    step="0.01"
                    value={oilFormData.minimum_stock_liters ?? ""}
                    onChange={(e) =>
                      setOilFormData({
                        ...oilFormData,
                        minimum_stock_liters: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="Opcional"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oil-description">Descripción</Label>
                  <Textarea
                    id="oil-description"
                    value={oilFormData.description}
                    onChange={(e) =>
                      setOilFormData({
                        ...oilFormData,
                        description: e.target.value,
                      })
                    }
                    rows={2}
                    placeholder="Descripción opcional..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleClose(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? "Creando..." : "Crear Aceite"}
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="material" className="mt-4">
            <form onSubmit={handleCreateMaterial}>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mat-code">Código *</Label>
                    <Input
                      id="mat-code"
                      value={materialFormData.code}
                      onChange={(e) =>
                        setMaterialFormData({
                          ...materialFormData,
                          code: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="BOT-500, TAP..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mat-unit">Unidad *</Label>
                    <Select
                      value={materialFormData.unit}
                      onValueChange={(value) =>
                        setMaterialFormData({
                          ...materialFormData,
                          unit: value,
                        })
                      }
                    >
                      <SelectTrigger id="mat-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MATERIAL_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mat-name">Nombre *</Label>
                  <Input
                    id="mat-name"
                    value={materialFormData.name}
                    onChange={(e) =>
                      setMaterialFormData({
                        ...materialFormData,
                        name: e.target.value,
                      })
                    }
                    placeholder="Botella 500ml, Tapa 38mm..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mat-min-stock">Stock Mínimo</Label>
                  <Input
                    id="mat-min-stock"
                    type="number"
                    min="0"
                    value={materialFormData.minimum_stock_level ?? ""}
                    onChange={(e) =>
                      setMaterialFormData({
                        ...materialFormData,
                        minimum_stock_level: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="Opcional"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mat-description">Descripción</Label>
                  <Textarea
                    id="mat-description"
                    value={materialFormData.description}
                    onChange={(e) =>
                      setMaterialFormData({
                        ...materialFormData,
                        description: e.target.value,
                      })
                    }
                    rows={2}
                    placeholder="Descripción opcional..."
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    id="mat-exclude-inventory"
                    checked={materialFormData.exclude_from_inventory ?? false}
                    onCheckedChange={(checked) =>
                      setMaterialFormData({
                        ...materialFormData,
                        exclude_from_inventory: checked,
                      })
                    }
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="mat-exclude-inventory">
                      Sin Inventario
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      No actualiza stock ni aparece en reportes
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleClose(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? "Creando..." : "Crear Material"}
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
